
// client/src/components/TaskList.jsx
// Standalone task list page — shows all tasks associated with the current user
// up to one year old. Managers/admins can see all tenant tasks.

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getSessionLocal, sessionRequestConfig } from "../services/sessionLocal";
import { useTranslation } from "react-i18next";
import AppFooter from "./AppFooter";
import SearchFilterBar from "./shared/SearchFilterBar";
import { FaArrowLeft, FaHome, FaSync } from "react-icons/fa";
import "./TaskList.css";

const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

const STATUS_OPTIONS = [
  { value: "",            label: "All Statuses" },
  { value: "active",      label: "Active" },
  { value: "pending",     label: "Pending" },
  { value: "in-progress", label: "In Progress" },
  { value: "completed",   label: "Completed" },
  { value: "blocked",     label: "Blocked" },
];

const PRIORITY_OPTIONS = [
  { value: "",       label: "All Priorities" },
  { value: "urgent", label: "Urgent" },
  { value: "high",   label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low",    label: "Low" },
];

function formatDate(value) {
  if (!value) return "\u2014";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return String(value);
  }
}

function statusBadgeClass(status) {
  switch ((status || "").toLowerCase()) {
    case "completed":   return "bg-success";
    case "in-progress": return "bg-info";
    case "blocked":     return "bg-danger";
    default:            return "bg-secondary";
  }
}

function priorityBadgeClass(priority) {
  switch ((priority || "").toLowerCase()) {
    case "urgent": return "bg-danger";
    case "high":   return "bg-warning";
    case "medium": return "bg-info";
    default:       return "bg-secondary";
  }
}

export default function TaskList() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const session = getSessionLocal();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  // Role-based visibility
  const role = (session.role || "").toLowerCase();
  const isManagerOrAbove = ["owner", "admin", "manager"].includes(role);

  // Read dashboard config for manager visibility setting
  const managersCanSeeAll = (() => {
    try {
      const raw = localStorage.getItem("dashboardTaskConfig");
      if (raw) return JSON.parse(raw).managersCanSeeAllTasks !== false;
    } catch { /* ignore */ }
    return true;
  })();
  const canSeeAllTasks = isManagerOrAbove && managersCanSeeAll;

  const fetchTasks = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const cfg = sessionRequestConfig();
      const params = { range: "year" };

      if (!canSeeAllTasks) {
        params.username = session.username;
      }

      // Pass date range if user set one (overrides the year range on the backend)
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo)   params.dateTo = dateTo;

      // Pass status filter
      if (statusFilter) params.status = statusFilter;

      const res = await axios.get(
        `${baseUrl}/api/events/${tenant}/tasks`,
        { ...cfg, params: { ...cfg.params, ...params } }
      );

      if (res.data?.success) {
        setTasks(res.data.tasks || []);
      } else {
        setTasks([]);
      }
    } catch (err) {
      console.error("Error loading tasks:", err);
      setError("Failed to load tasks");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tenant, canSeeAllTasks, session.username, dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    if (tenant) fetchTasks();
  }, [tenant, fetchTasks]);

  const clearFilters = () => {
    setSearchTerm("");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("");
    setPriorityFilter("");
  };

  // Client-side filtering for search term and priority (server handles date + status)
  const filtered = tasks.filter((task) => {
    // Search filter
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const title = (task.title || task.name || "").toLowerCase();
      const comment = (task.comment || "").toLowerCase();
      const assignee = (task.assignedToName || "").toLowerCase();
      const eventName = (task.eventName || "").toLowerCase();
      if (!title.includes(q) && !comment.includes(q) && !assignee.includes(q) && !eventName.includes(q)) {
        return false;
      }
    }
    // Priority filter (client-side)
    if (priorityFilter && (task.priority || "").toLowerCase() !== priorityFilter) {
      return false;
    }
    return true;
  });

  const handleTaskClick = (task) => {
    if (task.eventid) {
      const path = task.eventitemid
        ? `/${tenant}/${task.eventid}/${task.eventitemid}/eventItemDetail`
        : `/${tenant}/${task.eventid}/eventDetail`;
      navigate(path);
    }
  };

  return (
    <div className="tasklist-page tasklist-theme">
      {/* Header */}
      <div className="tasklist-header">
        <h2 className="hdr hdr--primary hdr--lg" style={{ display: "block", margin: 0, color: "#000" }}>
          {canSeeAllTasks
            ? t("taskList.allTasksTitle", "All Tasks")
            : t("taskList.myTasksTitle", "My Tasks")}
        </h2>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => fetchTasks(true)}
          disabled={refreshing}
          title="Refresh"
        >
          <FaSync className={refreshing ? "fa-spin" : ""} />
        </button>
      </div>

      {/* Task count */}
      <div className="tasklist-count">
        {!loading && (
          <small>
            {filtered.length} {filtered.length === 1 ? "task" : "tasks"}
            {filtered.length !== tasks.length && ` (${tasks.length} total)`}
          </small>
        )}
      </div>

      {/* Search & Date filter */}
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onClear={clearFilters}
        searchPlaceholder="Search tasks..."
      />

      {/* Status & Priority filter row */}
      <div className="tasklist-filter-row">
        <select
          className="form-select form-select-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          className="form-select form-select-sm"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
        >
          {PRIORITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Loading */}
      {loading && (
        <div className="tasklist-empty">
          <p>{t("common.loading", "Loading...")}</p>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="tasklist-empty">
          <p style={{ color: "#dc3545" }}>{error}</p>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && filtered.length === 0 && (
        <div className="tasklist-empty">
          <p>{t("taskList.noTasks", "No tasks found")}</p>
        </div>
      )}

      {/* Task list */}
      {!loading && !error && filtered.length > 0 && (
        <ul className="list-group tasklist-items">
          {filtered.map((task) => (
            <li
              key={task.id}
              className="list-group-item"
              data-priority={(task.priority || "medium").toLowerCase()}
              onClick={() => handleTaskClick(task)}
              style={{ cursor: task.eventid ? "pointer" : "default" }}
            >
              {/* Row 1: Title + Priority badge */}
              <div className="tasklist-item-row">
                <span className="tasklist-item-title">
                  {task.title || task.name || "Untitled Task"}
                </span>
                <span className={`badge ${priorityBadgeClass(task.priority)}`}>
                  {(task.priority || "medium").toUpperCase()}
                </span>
              </div>

              {/* Row 2: Event name */}
              {task.eventName && (
                <div className="tasklist-item-event">
                  {task.eventName}
                </div>
              )}

              {/* Row 3: Date + Assignee + Status */}
              <div className="tasklist-item-row tasklist-item-meta">
                <span className="tasklist-item-date">
                  {formatDate(task.taskdate)}
                  {canSeeAllTasks && task.assignedToName && (
                    <span className="tasklist-item-assignee">
                      {" \u00b7 "}{task.assignedToName}
                    </span>
                  )}
                </span>
                <span className={`badge ${statusBadgeClass(task.status)}`}>
                  {(task.status || "pending").toUpperCase()}
                </span>
              </div>

              {/* Row 4: Comment preview (if any) */}
              {task.comment && (
                <div className="tasklist-item-comment">
                  {task.comment.length > 80 ? task.comment.substring(0, 80) + "\u2026" : task.comment}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Footer */}
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: t("common.back", "Back"),
            onClick: () => navigate(-1),
          },
          {
            icon: <FaHome />,
            label: t("common.home", "Home"),
            onClick: () => navigate(`/${tenant}/dashboard`),
          },
        ]}
      />
    </div>
  );
}
